/*
 * Screenshot list shaping — records into per-image entries, and grouping.
 *
 * Ported from html-markup/assets/js/screenshots.js, with one difference the
 * markup could not have known about:
 *
 *   ONE Screenshot DOCUMENT CAN HOLD SEVERAL IMAGES.
 *
 * The desktop captures every attached display in a single tick, so the API
 * returns `screenshotUrls: [url, url]` under one record with one timestamp, one
 * window title and one set of counters. The markup's mock data had exactly one
 * image per record and so never distinguished the two.
 *
 * Every image is flattened into its own entry here, because an image is what
 * the grid shows and what the viewer's ←/→ step through — a card that silently
 * dropped the second monitor would hide half of a dual-screen user's day. The
 * counters are NOT divided across displays: `keystrokes` was measured for the
 * interval, not for a monitor, so both entries carry the same number and the
 * viewer labels them "Display 1 of 2" to say why.
 */
import { dayjs } from "@/lib/format.js";
import { hostOf } from "@/lib/format.js";

/*
 * Filtering, the day's figures and the application list all moved to the
 * SERVER — see applyScreenshotFilters/screenshotSummary in
 * backend/controllers/screenshotController.js. Doing them here meant fetching a
 * whole capped day to show one page. What is left is the shaping the browser
 * still has to do: records into per-image entries, and grouping the page.
 */
export const PRODUCTIVITY_OPTIONS = [
    { value: "", label: "All productivity" },
    { value: "productive", label: "Productive" },
    { value: "neutral", label: "Neutral" },
    { value: "unproductive", label: "Unproductive" },
    { value: "unrated", label: "Unrated" },
];

/**
 * API records → one entry per image.
 *
 * `fallbackUser` covers the per-user endpoint, whose response omits `user`
 * entirely (it was filtered by user id, so the server saw no reason to repeat
 * it). Without this the whole page would render "Unknown" the moment someone
 * picked a name from the filter.
 */
export function flattenShots(records = [], fallbackUser = null) {
    const shots = [];

    for (const record of records) {
        const urls = Array.isArray(record.screenshotUrls) ? record.screenshotUrls : [];
        const at = dayjs(record.timestamp);

        const user = record.user
            ? {
                  id: record.user.id ?? record.user._id,
                  name: record.user.name,
                  email: record.user.email,
              }
            : fallbackUser;

        // A record whose URLs all failed to sign still happened. It is kept with
        // a null src so the count stays truthful and ProxyImage shows its
        // "Not available" fallback, rather than the day quietly shrinking.
        const sources = urls.length ? urls : [null];

        sources.forEach((src, index) => {
            shots.push({
                key: `${record.id}#${index}`,
                recordId: record.id,
                src,
                // Every display of this capture. The grid shows one image per
                // card, but a row that represents the whole INTERVAL — the key
                // log — wants all of them.
                sources,
                display: index + 1,
                displayCount: sources.length,
                timestamp: record.timestamp,
                // Seconds included: a capture every few seconds means three
                // cards can share a minute, and a badge reading 8:45 PM on
                // all of them makes them look like the same moment.
                time: at.isValid() ? at.format("h:mm:ss A") : "—",
                hour: at.isValid() ? at.hour() : 0,
                // The calendar day, for grouping a range. Without it two days'
                // "5:00 PM" collapse into one section.
                day: at.isValid() ? at.format("YYYY-MM-DD") : "",
                dayLabel: at.isValid() ? at.format("DD MMM") : "—",
                blurred: Boolean(record.blurred),
                application: record.application || "Unknown",
                title: record.title || "",
                url: record.url || "",
                productivityStatus: record.productivityStatus || "unrated",
                keystrokes: record.keystrokes ?? 0,
                mouseClicks: record.mouseClicks ?? 0,
                mouseMovement: record.mouseMovement ?? 0,
                keyLog: record.keyLog ?? [],
                clipboard: record.clipboard ?? [],
                user: user ?? { id: "", name: "Unknown", email: "" },
            });
        });
    }

    return shots;
}

const hourLabel = (hour) => {
    const period = hour >= 12 ? "PM" : "AM";
    const h12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${h12}:00 ${period}`;
};

/**
 * Sections for the results body. Insertion order is kept, so with the API's
 * newest-first sort the hours read 6 PM, 5 PM, 4 PM — the way you scan a day
 * backwards from now.
 *
 * `multiDay` puts the date in front of an hour. Over a range, "5:00 PM" alone
 * would merge Monday's afternoon into Tuesday's and quietly report the pair as
 * one hour's work.
 */
export function groupShots(shots, groupBy, { multiDay = false } = {}) {
    if (groupBy === "none") return [{ key: "", label: "", items: shots }];

    const labelOf = (s) => {
        if (groupBy === "user") return s.user.name;
        if (groupBy === "day") return s.dayLabel;
        return multiDay ? `${s.dayLabel}, ${hourLabel(s.hour)}` : hourLabel(s.hour);
    };

    const map = new Map();
    for (const s of shots) {
        const label = labelOf(s);
        if (!map.has(label)) map.set(label, []);
        map.get(label).push(s);
    }

    return [...map.entries()].map(([label, items]) => ({
        key: label,
        label,
        items,
    }));
}

/*
 * CSV of the rows on screen.
 *
 * The image URL is deliberately NOT a column. It is a signed S3 link that
 * expires in fifteen minutes, so a spreadsheet full of them is a spreadsheet
 * full of dead links — and one that leaks read access to anyone it is forwarded
 * to for as long as it does work.
 */
export const SHOT_CSV_HEADERS = [
    "Time",
    "User",
    "Email",
    "Application",
    "Window title",
    "Host",
    "Productivity",
    "Display",
    "Blurred",
    "Keystrokes",
    "Clicks",
    "Mouse movement",
];

export function shotsToRows(shots) {
    return shots.map((s) => [
        s.time,
        s.user.name,
        s.user.email,
        s.application,
        s.title,
        s.url ? hostOf(s.url) : "",
        s.productivityStatus,
        s.displayCount > 1 ? `${s.display} of ${s.displayCount}` : "1",
        s.blurred ? "yes" : "no",
        s.keystrokes,
        s.mouseClicks,
        s.mouseMovement,
    ]);
}
